import UiContainer from "@/UI/Containers/UiContainer";
import { UiTableV2 } from "@repo/ui/index.tsx";
import { handleErrorToast } from "@/UI/Toasts";
import BreadCrumb from "@/components/BreadCrumb";
import ConfirmModal from "@/components/ConfirmModal";
import EmptyTableComp from "@/components/Empties/EmptyTableComp";
import PageWrapper from "@/components/Wrappers/PageWrapper";
import PermissionWrapper from "@/components/Wrappers/PermissionWrapper";
import { formatDate } from "@/utility/date.utility";
import {
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/24/solid";
import { ButtonBase } from "@repo/ui/index.tsx";
import { ColumnDef } from "@tanstack/react-table";
import { useEffect } from "react";
import AddEditGeoScopeModal from "./components/AddEditGeoScopeModal";
import { IGeoScopeResponseSchema } from "./geoscope.type";
import useGeoScopes from "./hooks/useGeoScopes";
import useCheckPermission from "@/Hooks/useCheckPermission";

function GeoScopes() {
  const {
    service: { allGeoScopeService },
    confirmModal: { isOpen: isConfirmModalOpen, toggler: toggleConfirm },
    mutation: {
      createGeoScopesMutation,
      updateGeoScopeMutation,
      deleteGeoScopeMutation,
    },
    states: {
      editData,
      isOpen,
      deleteRow,
      searchQuery,
      setSearchQuery,
      setDeleteRow,
      setEditData,
    },

    functions: {
      handleAddEditGeoScope,
      handleDeleteGeoScope,
      toggleAddEditModal,
    },
  } = useGeoScopes({ enablePagination: true, enablePerPage: true });
  const { hasPermission } = useCheckPermission();
  // Effect to display Toast message on Fail Api
  useEffect(() => {
    if (allGeoScopeService?.error) {
      handleErrorToast({ message: allGeoScopeService?.error?.message });
    }
  }, [allGeoScopeService?.error]);

  // Table Schema

  const columnsSchema: ColumnDef<IGeoScopeResponseSchema>[] = [
    {
      header: "#",
      cell: ({ cell }) => {
        return (
          <span>
            {cell.row.index + (+allGeoScopeService?.data?.meta.from || 1)}
          </span>
        );
      },
    },
    {
      header: "Name",
      accessorKey: "name",
    },
    {
      header: "Code",
      accessorKey: "code",
    },
    {
      header: "Created At",
      accessorKey: "created_at",
      cell: (cell) => {
        return formatDate(String(cell.getValue()));
      },
    },
    {
      header: "Update At",
      accessorKey: "updated_at",
      cell: (cell) => {
        return formatDate(String(cell.getValue()));
      },
    },
  ];
  if (hasPermission.update || hasPermission.delete) {
    columnsSchema.push({
      header: "Actions",
      cell: (cell) => {
        const rowData = cell.row.original;
        return (
          <div className="flex gap-2 items-center">
            <PermissionWrapper permission="update">
              <ButtonBase
                icon={<PencilSquareIcon className="size-4 text-body" />}
                className="h-7 w-7 text-body dark:text-white"
                buttonType="secondary"
                onClick={() => {
                  setEditData(rowData);
                  toggleAddEditModal();
                }}
              />
            </PermissionWrapper>
            <PermissionWrapper permission="delete">
              <ButtonBase
                icon={<TrashIcon className="size-4 text-error" />}
                className="h-7 w-7 text-body dark:text-white"
                buttonType="secondary"
                onClick={() => {
                  setDeleteRow(String(rowData?.id));
                  toggleConfirm();
                }}
              />
            </PermissionWrapper>
          </div>
        );
      },
    });
  }
  return (
    <PageWrapper className="flex flex-col gap-2">
      <BreadCrumb />
      <UiContainer className="flex items-center justify-between">
        <section>
          <h1 className="capitalize text-md text-subHeading font-semibold">
            Geo Scopes
          </h1>
          <p className="text-sm font-medium text-body">
            List of all Geo Scopes
          </p>
        </section>
        <PermissionWrapper permission="create">
          <ButtonBase
            text="Add Geo Scope"
            buttonType="primary"
            icon={<PlusIcon className="size-6" />}
            className="w-40"
            onClick={toggleAddEditModal}
          />
        </PermissionWrapper>
      </UiContainer>
      <UiContainer className="mt-8">
        <UiTableV2
          columnsSchema={columnsSchema}
          dataSet={allGeoScopeService}
          searchPlaceHolder="Search for a geoscope"
          query={searchQuery}
          setQuery={setSearchQuery}
          tableLabel="List of Geo Scopes"
          tableEmptyComponent={
            <PermissionWrapper permission="create">
              <EmptyTableComp
                text="No Geo Scope found!"
                onClick={toggleAddEditModal}
                buttonText="Add"
              />
            </PermissionWrapper>
          }
        />
      </UiContainer>

      {isOpen && (
        <AddEditGeoScopeModal
          handleClose={toggleAddEditModal}
          isOpen={isOpen}
          isLoading={
            createGeoScopesMutation.isPending ||
            updateGeoScopeMutation.isPending
          }
          onSubmitHandler={handleAddEditGeoScope}
          editData={editData}
        />
      )}
      {isConfirmModalOpen && (
        <ConfirmModal
          isOpen={isConfirmModalOpen}
          handleCloseModal={toggleConfirm}
          handleDelete={() => handleDeleteGeoScope(String(deleteRow))}
          isLoading={deleteGeoScopeMutation.isPending}
        />
      )}
    </PageWrapper>
  );
}

export default GeoScopes;
